import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Lock, Mail, KeyRound, RefreshCw, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  
  // OTP verification state
  const [requiresOtp, setRequiresOtp] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpData, setOtpData] = useState(null); // { user_id, ip_address, email_hint }
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      
      // Check if OTP is required (new device)
      if (response.data.requires_otp) {
        setRequiresOtp(true);
        setOtpData({
          user_id: response.data.user_id,
          ip_address: response.data.ip_address,
          email_hint: response.data.email_hint
        });
        toast.info('New device detected. OTP sent to your email.');
        setLoading(false);
        return;
      }
      
      const user = response.data.user;
      
      // Only allow platform super admins
      if (user.role !== 'platform_super_admin') {
        toast.error('This portal is for platform administrators only');
        setLoading(false);
        return;
      }
      
      toast.success('Welcome to WellNest Technologies Platform');
      onLogin(user, response.data.token);
      navigate('/platform');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-admin-otp`, {
        user_id: otpData.user_id,
        otp: otp,
        ip_address: otpData.ip_address
      });
      
      if (response.data.success) {
        toast.success('Device verified successfully!');
        onLogin(response.data.user, response.data.token);
        navigate('/platform');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-admin-otp`, {
        user_id: otpData.user_id,
        ip_address: otpData.ip_address
      });
      toast.success('New OTP sent to your email');
    } catch (error) {
      toast.error('Failed to resend OTP. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = () => {
    setRequiresOtp(false);
    setOtp('');
    setOtpData(null);
  };

  // OTP Verification Screen
  if (requiresOtp) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 w-full max-w-md">
          <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-800 p-8 shadow-2xl">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/25 mb-4">
                <KeyRound className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                Verify Your Identity
              </h1>
              <p className="text-slate-400 text-sm">
                New device detected. Enter the OTP sent to
              </p>
              <p className="text-amber-400 font-medium mt-1">
                {otpData?.email_hint || '***@wellnesttechnologies.com'}
              </p>
            </div>

            <form onSubmit={handleOtpVerify} className="space-y-5">
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" strokeWidth={1.5} />
                <Input
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="pl-11 h-14 rounded-lg bg-slate-800 border-slate-700 text-white text-center text-2xl tracking-widest placeholder:text-slate-500 placeholder:text-base placeholder:tracking-normal focus:border-amber-500 focus:ring-amber-500/20"
                  required
                  maxLength={6}
                  autoFocus
                  data-testid="admin-otp-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-lg text-base bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold"
                disabled={loading || otp.length !== 6}
                data-testid="verify-otp-button"
              >
                {loading ? 'Verifying...' : 'Verify & Login'}
              </Button>
            </form>

            <div className="mt-6 flex items-center justify-between">
              <button 
                onClick={handleBackToLogin}
                className="text-slate-400 hover:text-white text-sm flex items-center gap-1 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </button>
              
              <button 
                onClick={handleResendOtp}
                disabled={resending}
                className="text-amber-400 hover:text-amber-300 text-sm flex items-center gap-1 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${resending ? 'animate-spin' : ''}`} />
                {resending ? 'Sending...' : 'Resend OTP'}
              </button>
            </div>

            <div className="mt-6 p-3 bg-slate-800/50 rounded-lg">
              <p className="text-slate-500 text-xs text-center">
                🔒 This security measure protects your admin account from unauthorized access on new devices.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Login Screen
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl" />
      </div>
      
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-800 p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center shadow-lg shadow-red-500/25 mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Platform Administration
            </h1>
            <p className="text-slate-400 text-sm">
              WellNest Technologies Control Center
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="super-admin-login-form">
            <div className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" strokeWidth={1.5} />
                <Input
                  type="email"
                  placeholder="Admin Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-11 h-12 rounded-lg bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-red-500 focus:ring-red-500/20"
                  required
                  data-testid="admin-email-input"
                />
              </div>

              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" strokeWidth={1.5} />
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-11 h-12 rounded-lg bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-red-500 focus:ring-red-500/20"
                  required
                  data-testid="admin-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 rounded-lg text-base bg-gradient-to-r from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700 text-white font-semibold"
              disabled={loading}
              data-testid="admin-login-button"
            >
              {loading ? 'Authenticating...' : 'Access Platform'}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <p className="text-slate-600 text-xs">
              Authorized personnel only. All access is logged.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminLogin;
